/**
 * @file     colors.ts
 * @readme   ./README.md
 * @purpose  Defines the full light and dark color palettes (brand, primary, secondary, accent, neutral, semantic, state, interaction) keyed by ThemeMode.
 */
import type { ColorPalette, ThemeMode } from '@/core/types';

const lightPalette: ColorPalette = {
  brand: {
    primary: '#005886',
    secondary: '#F4A259',
    accent: '#2F8F9D'
  },
  primary: {
    0: '#F2F9FD',
    50: '#E5F1F8',
    100: '#C1DFEE',
    200: '#97C9E1',
    300: '#66AFD0',
    400: '#338EB7',
    500: '#005886',
    600: '#004A72',
    700: '#003B5B',
    800: '#002C44',
    900: '#001D2D'
  },
  secondary: {
    0: '#FFFAF4',
    50: '#FFF2E4',
    100: '#FFE0BF',
    200: '#FFD09F',
    300: '#FFBF7E',
    400: '#FFB56A',
    500: '#F4A259',
    600: '#D2833C',
    700: '#AA6828',
    800: '#804D19',
    900: '#57330D'
  },
  accent: {
    0: '#F3FBFD',
    50: '#E8F6FA',
    100: '#CBEAF2',
    200: '#A8DAE8',
    300: '#84CADF',
    400: '#5FB8D2',
    500: '#3FA5C3',
    600: '#2F8F9D',
    700: '#236E78',
    800: '#184E54',
    900: '#0D2E33'
  },
  neutral: {
    0: '#FFFFFF',
    50: '#F8F9FB',
    100: '#E8ECF2',
    200: '#D4DAE4',
    300: '#BFC8D4',
    400: '#99A5B8',
    500: '#7F8EA1',
    600: '#647286',
    700: '#4D5A6B',
    800: '#354150',
    900: '#1D2733'
  },
  semantic: {
    background: '#F8F9FB',
    surface: '#FFFFFF',
    // Light mode inverts the recessed/input relationship: cards are white on a
    // tinted page, so recessed panels tint and inputs stay white.
    surfaceRecessed: '#F8F9FB',
    surfaceInput: '#FFFFFF',
    border: '#C8D0DB',
    divider: '#B0BAC8',
    textPrimary: '#1D2733',
    textSecondary: '#3D4A5C',
    textMuted: '#566478'
  },
  state: {
    success: '#1B9C73',
    warning: '#D7881E',
    error: '#D94F4F',
    info: '#2A79C5'
  },
  interaction: {
    hoverSurface: '#E8ECF2',
    hoverElevated: '#EFF2F6',
    pressedSurface: '#D4DAE4',
    // Brand primary: the teal ring measured 1.7:1 on white, under the 3:1 floor.
    focusRing: '#005886',
    accentYellow: '#B8860B',
    accentYellowMuted: 'rgba(184, 134, 11, 0.1)'
  }
};

const darkPalette: ColorPalette = {
  brand: {
    primary: '#4DB2BA',
    secondary: '#FFBA75',
    accent: '#69C8D9'
  },
  primary: {
    0: '#08191C',
    50: '#0D2327',
    100: '#133036',
    200: '#19414A',
    300: '#21525D',
    400: '#2E6A77',
    500: '#3D8797',
    600: '#4DB2BA',
    700: '#77C6CD',
    800: '#A0DCE0',
    900: '#CBEFEF'
  },
  secondary: {
    0: '#231A10',
    50: '#322314',
    100: '#47331E',
    200: '#5D4429',
    300: '#745635',
    400: '#8E6A42',
    500: '#AA8051',
    600: '#C59661',
    700: '#DFAC71',
    800: '#FFBA75',
    900: '#FFD6A9'
  },
  accent: {
    0: '#0B1A1E',
    50: '#10262B',
    100: '#16343C',
    200: '#1F4651',
    300: '#2A5C69',
    400: '#377684',
    500: '#4892A3',
    600: '#69C8D9',
    700: '#92D7E4',
    800: '#B8E7EE',
    900: '#D7F4F8'
  },
  neutral: {
    0: '#F7FAFD',
    50: '#E9EFF6',
    100: '#CFD8E3',
    200: '#ACB9CA',
    300: '#8C9AAF',
    400: '#6D7D93',
    500: '#55657A',
    600: '#3E4D61',
    700: '#2D394A',
    800: '#1D2733',
    900: '#060A0F'
  },
  semantic: {
    background: '#080D12',
    surface: '#111A24',
    // One step darker than surface: data panels read as recessed instrument
    // panels, inputs sit a step below the card they live on.
    surfaceRecessed: '#0D1620',
    surfaceInput: '#0E1822',
    border: '#243243',
    divider: '#33465D',
    textPrimary: '#F2F6FA',
    textSecondary: '#B8C6D5',
    textMuted: '#8C9AAF'
  },
  state: {
    success: '#49D9A3',
    warning: '#FFB14B',
    error: '#FF7A7A',
    info: '#78B8F5'
  },
  interaction: {
    hoverSurface: '#182330',
    hoverElevated: '#1C2937',
    pressedSurface: '#223245',
    focusRing: '#4DB2BA',
    accentYellow: '#D4A017',
    accentYellowMuted: 'rgba(212, 160, 23, 0.15)'
  }
};

export const colorPalettesByMode: Record<ThemeMode, ColorPalette> = {
  light: lightPalette,
  dark: darkPalette
};

/** Elevated surface shadow (toasts, popovers): mode-agnostic, deliberately black-based. */
export const shadowElevated = '0 6px 20px rgba(0, 0, 0, 0.14)';
